import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axiosInstance from '../../api/axiosInstance';
import Editor from "@monaco-editor/react";
import ImagePreviewModal from './ImagePreviewModal';
import TestCaseAutoUpdateModal from './TestCaseAutoUpdateModal';

const FAILURE_SUMMARY_MARKER = 'ROBITEST_FAILURE_SUMMARY::';

const extractFailureMessage = (payloadLogs, apiMessage) => {
  if (typeof apiMessage === 'string' && apiMessage.trim()) {
    return apiMessage.trim();
  }
  const text = typeof payloadLogs === 'string' ? payloadLogs : '';
  if (!text) return null;

  for (const line of text.split('\n')) {
    if (line.startsWith(FAILURE_SUMMARY_MARKER)) {
      const value = line.slice(FAILURE_SUMMARY_MARKER.length).trim();
      if (value) return value;
    }
  }

  const reasonMatch = text.match(/(?:❌\s*)?Execution Failed!\s*Reason:\s*(.+)/i);
  if (reasonMatch?.[1]) {
    return reasonMatch[1].trim();
  }

  const crashMatch = text.match(/"error_message"\s*:\s*"([^"]+)"/);
  if (crashMatch?.[1]) {
    return crashMatch[1].trim();
  }

  return null;
};

const TestScreenshotsModal = ({ 
  isOpen, 
  onClose, 
  executionId, 
  testCaseName,
  testCaseId,
  onSelfHealingSuccess
}) => {
  const [screenshots, setScreenshots] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [logs, setLogs] = useState(null);
  const [failureMessage, setFailureMessage] = useState(null);
  const [isAutoUpdateModalOpen, setIsAutoUpdateModalOpen] = useState(false);


  // Fetch screenshots from API
  const fetchScreenshots = useCallback(async () => {
    if (!executionId) return;

    setIsLoading(true);
    setError(null);

    try {
      // TEMPORARY: Using dummy response for testing
      // TODO: Uncomment the actual API call below once backend is deployed
      // const response = {
      //   status: 200,
      //   screenshots: [
      //     "https://dtp-robitest.s3.amazonaws.com/develop/executions/4036/f38dd564-dee4-4d46-8c84-4952c18b3770.png",
      //     "https://dtp-robitest.s3.amazonaws.com/develop/executions/4036/b6bee9a5-1fb8-4512-989d-609eb2efabe8.png",
      //   ],
      //   // logs : null
      //   logs: "\n\n--- Standard Error ---\nError: name 'pag_selector' is not defined\nTraceback (most recent call last):\n  File \"/tmp/cloud_run_ff544c.py\", line 61, in <module>\n    run()\n  File \"/tmp/cloud_run_ff544c.py\", line 48, in run\n    pag_selector(\"input[placeholder='Search projects...']\", state=\"visible\", timeout=10000)\n    ^^^^^^^^^^^^\nNameError: name 'pag_selector' is not defined\n"
      // };

      // Actual API call (commented out for now):
      const response = await axiosInstance.get(
        `/test_case/list_test_screenshots?execution_id=${executionId}&test_case_id=${testCaseId}`
      );

      if (response.status === 200) {
        const nextLogs = response?.logs || null;
        setScreenshots(response?.screenshots || []);
        setLogs(nextLogs);
        setFailureMessage(
          extractFailureMessage(nextLogs, response?.error_message)
        );
      } else {
        throw new Error('Failed to fetch screenshots');
      }
    } catch (err) {
      console.error('Error fetching screenshots:', err);
      setError(err.message || 'Failed to load screenshots');
      setFailureMessage(null);
    } finally {
      setIsLoading(false);
    }
  }, [executionId, testCaseId]);

  useEffect(() => {
    if (isOpen && executionId) {
      fetchScreenshots();
    }
  }, [isOpen, executionId, fetchScreenshots]);

    const handleAutoUpdateClick = () => {
    setIsAutoUpdateModalOpen(true);
  };

  const handleApplyUpdates = (updateData) => {
    // Show success toast notification
    // const toast = document.createElement('div');
    // toast.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2 animate-pulse';
    // toast.innerHTML = `
    //   <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    //     <polyline points="20 6 9 17 4 12"></polyline>
    //   </svg>
    //   <span>Script applied successfully!</span>
    // `;
    // document.body.appendChild(toast);
    // setTimeout(() => {
    //   toast.remove();
    // }, 3000);

    // // Handle success with optional callback
    // if (onSelfHealingSuccess) {
    //   onSelfHealingSuccess({
    //     message: 'Script applied successfully',
    //     testCaseId: testCaseId
    //   });
    // }
  };
  

  const handleImageClick = useCallback((imageSrc) => {
    setPreviewImage(imageSrc);
    setIsPreviewOpen(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setIsPreviewOpen(false);
    setTimeout(() => setPreviewImage(null), 300);
  }, []);

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Overlay */}
      <div data-rt-modal-overlay className="fixed inset-0 bg-black bg-opacity-50"
        style={{
          zIndex:16000
        }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div data-rt-modal-overlay className="fixed inset-0 flex items-center justify-center p-4"
          style={{
          zIndex:16500
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="screenshots-modal-title"
       onClick={(e) => { if (e.target === e.currentTarget) { const b = e.currentTarget.querySelector('[data-rt-modal-close], .close-btn, .modal-close, button[aria-label="Close"], button[aria-label="close"]'); if (b) b.click(); } }}>
        <div
          className="rt-results-modal rounded-lg shadow-2xl w-full"
          style={{ width: '75%', maxHeight: '90vh' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 p-6">
            <div>
              <h2 
                id="screenshots-modal-title"
                className="text-xl font-bold text-gray-900"
              >
                {testCaseName}
              </h2>
              {/* <p className="text-sm text-gray-600 mt-1">
                {testCaseName}
              </p> */}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="overflow-y-auto p-6" style={{ maxHeight: 'calc(90vh - 140px)' }}>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div
                    className="inline-block w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mb-4"
                    role="status"
                    aria-live="polite"
                  />
                  <p className="text-gray-600">Loading screenshots...</p>
                </div>
              </div>
            ) : error && screenshots.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mx-auto text-gray-400 mb-4"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                  <p className="text-gray-600">{error}</p>
                </div>
              </div>
            ) : (
              <>
                {failureMessage && (
                  <div
                    className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3"
                    role="alert"
                  >
                    <div className="text-xs font-semibold uppercase tracking-wide text-red-700 mb-1">
                      Failure reason
                    </div>
                    <p className="m-0 text-sm text-red-900 leading-relaxed break-words">
                      {failureMessage}
                    </p>
                  </div>
                )}

                {/* Logs Section */}
                {logs && (
                  <div className="mb-8">
                    <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-red-500"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                      Execution Logs
                    </h3>
                    <button 
                    onClick={handleAutoUpdateClick}
                      style={{ padding: "6px 12px", backgroundColor: "white", border: "1px solid #ccc", borderRadius: "4px", cursor: "pointer" }}
                    >Self-healing</button>
                    </div>

                    <div
                      className="border border-gray-300 rounded-lg overflow-hidden bg-gray-50"
                      style={{ height: '150px' }}
                    >
                      <Editor
                        height="100%"
                        defaultLanguage="plaintext"
                        theme="vs-dark"
                        value={logs}
                        options={{
                          minimap: { enabled: false },
                          scrollBeyondLastLine: false,
                          fontSize: 12,
                          automaticLayout: true,
                          readOnly: true,
                          wordWrap: 'on',
                          folding: true,
                          lineNumbers: 'on',
                        }}
                        beforeMount={(monaco) => {
                          monaco.editor.defineTheme("logsTheme", {
                            base: "vs",
                            inherit: true,
                            rules: [
                              {
                                token: 'number',
                                foreground: 'D7BA7D',
                              },
                            ],
                            colors: {
                              'editor.background': '#f9fafb',
                              'editor.foreground': '#1f2937',
                              'editorLineNumber.foreground': '#d1d5db',
                              'editor.lineHighlightBackground': '#f3f4f6',
                              'editor.selectionBackground': '#dbeafe',
                            },
                          });
                          monaco.editor.setTheme("logsTheme");
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Screenshots Section */}
                {screenshots.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-blue-500"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                      Screenshots ({screenshots.length})
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {screenshots.map((screenshot, index) => (
                        <ScreenshotCard
                          key={`${executionId}-${index}`}
                          screenshot={screenshot}
                          index={index}
                          onPreview={handleImageClick}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* No Content Message */}
                {screenshots.length === 0 && !logs && (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="mx-auto text-gray-400 mb-4"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                      <p className="text-gray-600">No screenshots or logs available</p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      <ImagePreviewModal
        isOpen={isPreviewOpen}
        imageSrc={previewImage}
        onClose={handleClosePreview}
      />
       <TestCaseAutoUpdateModal
        isOpen={isAutoUpdateModalOpen}
        onClose={() => setIsAutoUpdateModalOpen(false)}
        testCaseId={testCaseId}
        onSuccess={handleApplyUpdates}
      />
    </>
  );
};

/**
 * ScreenshotCard Component - Optimized screenshot thumbnail
 * Memoized to prevent unnecessary re-renders
 */
const ScreenshotCard = React.memo(({ screenshot, index, onPreview }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const handleImageError = useCallback(() => {
    setImageError(true);
    setImageLoaded(true);
  }, []);

  const handleClick = useCallback(() => {
    onPreview(screenshot);
  }, [screenshot, onPreview]);

 const handleDownload = useCallback((e) => {
  e.stopPropagation(); // prevent preview open

  const link = document.createElement('a');
  link.href = screenshot;
  link.download = screenshot.split('/').pop() || `screenshot-${index + 1}.png`;
  link.target = '_blank';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}, [screenshot, index]);


  return (
    <div
      className="group relative overflow-hidden rounded-lg border border-gray-200 bg-gray-50 aspect-video cursor-pointer transition-all duration-300 hover:shadow-lg hover:border-blue-400"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      aria-label={`Screenshot ${index + 1}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
    >
      {/* Loading Skeleton */}
      {!imageLoaded && (
        <div className="absolute inset-0 bg-gradient-to-r from-gray-100 via-gray-50 to-gray-100 animate-pulse" />
      )}

      {/* Image */}
      {!imageError ? (
        <img
          src={screenshot}
          alt={`Screenshot ${index + 1}`}
          className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gray-100">
          <span className="text-gray-400 text-sm">Image failed</span>
        </div>
      )}

      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300" />

      <div className="absolute top-2 right-2 flex items-center gap-2 z-10">
        <div className="bg-black bg-opacity-60 text-white px-2 py-1 rounded text-xs font-medium">
          {index + 1}
        </div>

        <button
          onClick={handleDownload}
          className="bg-black bg-opacity-60 hover:bg-opacity-80 text-white p-1.5 rounded transition"
          title="Download screenshot"
          aria-label="Download screenshot"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        </button>
      </div>
    </div>
    
  );
});

ScreenshotCard.displayName = 'ScreenshotCard';

export default TestScreenshotsModal;
